<template>
  <p
    class="body-3-bold tool-desc md:text-center sm:text-left sm:text-base pb-4"
  >
    {{ $t("calculator-suite.currencyDesc") }}
  </p>
  <div class="calculator-params-results">
    <section class="params-card">
      <h2 class="body-3-bold">{{ $t("calculator-suite.setParams") }}</h2>
      <div class="inputs">
        <div class="currency-input p-inputgroup flex w-full gap-3 md:gap-4">
          <InputSearch
            id="calculator-suite-currency-base"
            v-model="baseCurrency.currency"
            name="baseCurrency.currency"
            class="w-full"
            :label="$t('calculator-suite.currency')"
            :placeholder="$t('calculator-suite.selectCurrency')"
            :options="
              currenciesConvertor.map((c) => ({
                text: c.text,
                value: c.value,
              }))
            "
          />
          <TVInputNumber
            v-model="baseCurrency.value"
            :label="$t('calculator-suite.value')"
            :placeholder="$t('calculator-suite.amount')"
            name="baseCurrency.value"
            type="number"
            class="w-full"
          />
        </div>
      </div>
      <h2 class="body-3-bold">{{ $t("calculator-suite.chooseConv") }}</h2>

      <div class="flex flex-column w-full">
        <div
          v-for="(input, index) in params"
          :key="`currency-${index}`"
          class="p-inputgroup flex w-full gap-3 md:gap-4"
        >
          <InputSearch
            :id="`currency-${index}`"
            v-model="input.currency"
            :name="`currency-${index}`"
            :label="$t('calculator-suite.currency')"
            :placeholder="$t('calculator-suite.selectCurrency')"
            :model-value="input.currency"
            :options="
              currenciesConvertor.map((c) => ({
                text: c.text,
                value: c.value,
              }))
            "
            class="w-full"
          />
          <div v-if="isCalculating" class="w-full">
            <CurrencySkeleton class="p-3" />
          </div>
          <TVInputNumber
            v-else
            v-model="input.amount"
            :label="$t('calculator-suite.value')"
            :placeholder="$t('calculator-suite.amount')"
            :name="`amount-${index}`"
            class="pointer-events-none w-full"
            :disabled="true"
          />
        </div>
      </div>

      <button
        class="primary-button-medium"
        @click="calculator"
        :disabled="!isFormValid || isCalculating"
      >
        {{ $t("calculator-suite.calculate") }}
      </button>
    </section>
  </div>
</template>

<script setup>
import { ref, defineEmits, computed } from "vue";
import TVInputNumber from "../InputNumber.vue";
import InputSearch from "../InputSearch.vue";
import CurrencySkeleton from "./CurrencySkeleton.vue";
import { useI18n } from "vue-i18n";
import { currenciesConvertor } from "./utils/dropdownOptions";
import { convertCurrencyCalculate } from "./utils/calculateRequest";

const { t } = useI18n();
const baseCurrency = ref({ currency: null, value: null });
const emit = defineEmits(["isLoading"]);

const params = ref(
  Array(3)
    .fill()
    .map(() => ({
      currency: null,
      amount: null,
    }))
);

const isCalculating = ref(false);

const isFormValid = computed(() => {
  const isBaseValid =
    !!baseCurrency.value.currency && baseCurrency.value.value !== null;
  const hasConversionCurrency = params.value.some((param) => !!param.currency);

  return isBaseValid && hasConversionCurrency;
});

const calculator = async () => {
  isCalculating.value = true;

  const body = {
    action: "convert",
    base_currency: {
      currency: baseCurrency.value.currency,
      value: Number(baseCurrency.value.value),
    },
    conversion_currencies: params.value
      .filter((param) => param.currency)
      .map((param) => ({ currency: param.currency })),
  };
  try {
    const result = await convertCurrencyCalculate(body);

    const resultsMap = new Map();
    result.conversion_results.forEach((item) => {
      resultsMap.set(item.currency, item.value);
    });

    params.value = params.value.map((param) => {
      const convertedValue = resultsMap.get(param.currency);
      return {
        ...param,
        amount: convertedValue ?? null,
      };
    });
  } catch (error) {
    console.log(error);
    throw error;
  } finally {
    isCalculating.value = false;
  }
};
</script>
